import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectDocumentationActiveObjectData,
  setActiveObjectElementsRawData,
  setDependenciesConfigurationItem,
  setObjectName,
  setSingleObjectName,
  setTab,
  updateOrAddFieldToParsedConfigurationItems,
} from '../../reducers/documentation/documentationReducer';
import { DocumentationTabTypes } from '../../types/enums/DocumentationTabTypes';
import { useCrmOrgsApiFacade } from '../../apis/facades/useCrmOrgsApiFacade';
import { ConfigurationType } from '@server/parser';
import {
  selectEnvironments,
  selectParsedFields,
  selectParsedRecordTypes,
} from '../../reducers/global/globalReducer';
import { setSfFieldName } from '../pages/rollups/rollupHelpers';
import useParser from '../parser/useParser';
import { ACTIONS_EVENTS } from '../../services/events';
import useSendBiEvent from '../../hooks/useSendBiEvent';
import { telemetry } from '../../telemetry';
import { DependenciesContentViewType } from './dependencies/types';
import { useGetFilteredParsedRules } from './selected-object/filters/useGetFilteredParsedRules';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';
import { ConfigurationItem } from './dependencies/DependenciesTypes';
import { clearTopBarState } from '../../reducers/documentation/documentationTopBarReducer';
import { useDocumentationFiltersContext } from './selected-object/filters/DocumentationFiltersContext';

export type onFieldClickProps = {
  objectName: string;
  fieldName: string;
  id: string;
  crmOrgId: string;
  isRollup: boolean;
  contentType: DependenciesContentViewType;
};

export const useDocumentation = () => {
  const [isLoadingSingleField, setIsLoadingSingleField] = useState(false);
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId) ?? '';
  const { clearAllFilterValues } = useDocumentationFiltersContext();

  const sendBiEvent = useSendBiEvent();

  const dispatch = useDispatch();
  const { get_crmOrgField } = useCrmOrgsApiFacade();
  const { parseObject, objectsBeingParsed, toggleObjectVisibility } = useParser({
    crmOrgId,
  });

  const globalEnvironments = useSelector(selectEnvironments);
  const fields = useSelector(selectParsedFields(crmOrgId));
  const allElements = useSelector(selectDocumentationActiveObjectData);
  const relatedObjects = useMemo(
    () => allElements?.[DocumentationTabTypes.RELATED_OBJECTS],
    [allElements],
  );

  const parsedRecordTypes = useSelector(selectParsedRecordTypes(crmOrgId));
  const parsedRules = useGetFilteredParsedRules(crmOrgId);

  const getRollup = useCallback(
    ({
      fieldName,
      objectName,
      crmOrgId,
    }: {
      fieldName: string;
      objectName: string;
      crmOrgId: string;
    }) => {
      const rollups = globalEnvironments?.[crmOrgId]?.data?.rollups;
      return rollups?.find((rollup) => rollup.name === setSfFieldName(fieldName, objectName));
    },
    [globalEnvironments],
  );

  const getParsedField = useCallback(
    (objectName: string, fieldName: string) => {
      return (
        fields?.[objectName]?.find((field) => field.name === fieldName) ||
        relatedObjects?.find((ro) => ro.name === fieldName && ro.objectName === objectName)
      );
    },
    [fields, relatedObjects],
  );

  const handleRollup = useCallback(
    ({ fieldName, objectName, crmOrgId, contentType }: onFieldClickProps) => {
      const rollup = getRollup({ fieldName, objectName, crmOrgId });

      if (rollup) {
        dispatch(
          setDependenciesConfigurationItem({
            id: rollup?.rollupId,
            parentType: ConfigurationType.rollups,
            dependencyType: rollup.fieldType,
            name: fieldName,
            objectName,
            clearHistory: true,
            contentType,
          }),
        );
      } else {
        telemetry.captureError(new Error('Field marked as rollup was not found: '), {
          fieldName,
          objectName,
          crmOrgId,
        });
      }
    },
    [getRollup, dispatch],
  );

  const onFieldClick = useCallback(
    async (props: onFieldClickProps) => {
      const { isRollup, fieldName, objectName, crmOrgId, contentType, id } = props;

      if (isRollup) {
        handleRollup(props);
        return;
      }

      const existingParsedField = getParsedField(objectName, fieldName);

      if (existingParsedField) {
        dispatch(
          setDependenciesConfigurationItem({
            id: id ?? existingParsedField.id,
            parentType: ConfigurationType.fields,
            dependencyType: existingParsedField.type,
            name: fieldName,
            objectName,
            clearHistory: true,
            contentType,
          }),
        );
        return;
      }

      setIsLoadingSingleField(true);
      try {
        const field = await get_crmOrgField({ crmOrgId, objectApiName: objectName, fieldName });

        dispatch(
          updateOrAddFieldToParsedConfigurationItems({
            crmOrgId,
            field,
            objectName,
          }),
        );

        dispatch(
          setDependenciesConfigurationItem({
            id: field.id,
            parentType: ConfigurationType.fields,
            dependencyType: field.type,
            name: fieldName,
            objectName,
            clearHistory: true,
            contentType,
          }),
        );
      } catch (e) {
        telemetry.captureError(e);
      }
      setIsLoadingSingleField(false);
    },
    [dispatch, get_crmOrgField, getParsedField, handleRollup],
  );

  const onTabChange = useCallback(
    (event: React.SyntheticEvent<Element, Event>, activeTab: string) => {
      clearAllFilterValues();
      dispatch(setTab({ tab: activeTab as DocumentationTabTypes }));
      dispatch(clearTopBarState());
    },
    [clearAllFilterValues, dispatch],
  );

  const _clearTopBarState = useCallback(() => {
    dispatch(clearTopBarState());
  }, [dispatch]);

  const onAddObject = useCallback(
    async (objectName: string) => {
      sendBiEvent({
        name: ACTIONS_EVENTS.documentationAddObject,
        props: { object: objectName },
      });

      try {
        await parseObject(objectName);
      } catch (e) {
        telemetry.captureError(e);
      }
    },
    [parseObject, sendBiEvent],
  );

  const onRemoveObject = useCallback(
    async (objectName: string) => {
      sendBiEvent({
        name: ACTIONS_EVENTS.documentationAddObject,
        props: { object: objectName },
      });

      return await toggleObjectVisibility(objectName, false, true);
    },
    [toggleObjectVisibility, sendBiEvent],
  );

  const setActiveObjectElements = useCallback(
    (list: { [key in DocumentationTabTypes]: ConfigurationItem[] }) => {
      dispatch(setActiveObjectElementsRawData({ list }));
    },
    [dispatch],
  );

  const openOnFieldsTable = useCallback(() => {
    dispatch(setTab({ tab: DocumentationTabTypes.FIELDS }));
  }, [dispatch]);

  const dispatchSingleObjectName = useCallback(
    ({
      singleObjectName,
      tab,
    }: {
      singleObjectName?: ObjectTypeName;
      tab?: DocumentationTabTypes;
    }) => {
      if (!tab) {
        dispatch(setObjectName({ objectTypeName: singleObjectName }));
      } else {
        dispatch(setSingleObjectName({ singleObjectName, tab }));
      }
    },
    [dispatch],
  );

  return {
    _clearTopBarState,
    isLoadingSingleField,
    onFieldClick,
    openOnFieldsTable,

    onTabChange,
    getRollup,

    parsedRules,
    parsedRecordTypes,

    objectsBeingParsed,
    onAddObject,
    onRemoveObject,

    setActiveObjectElements,
    dispatchSingleObjectName,
  };
};
